'use client';

import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { datadogRum } from '@datadog/browser-rum';

import {
  RemotelockPin,
  Settings,
  GuestDTO,
  ReservationWithRateDetailsDTO,
} from '@/models';
import { ClientRemotelockService } from '@/services/client';

import NotAvailableStatus from '@/components/theme/not-available-status';

import { DigitalKeysAvailable } from './components/DigitalKeysAvailable';

interface DigitalKeysProps {
  company: string;
  guestId: string;
  settings: Settings;
  reservation: ReservationWithRateDetailsDTO | null;
  preview?: boolean;
}

export const DigitalKeys = ({
  company,
  settings,
  guestId,
  reservation,
  preview = false,
}: DigitalKeysProps): React.ReactElement | null => {
  const intl = useIntl();
  const [loading, setLoading] = useState<boolean>(true);
  const [remotelockDoors, setRemotelockDoors] = useState<RemotelockPin[]>([]);
  const reservationId = reservation?.id;
  const reservationCheckedOut =
    reservation &&
    ['checked_out', 'cancelled', 'no_show'].includes(reservation.status);

  useEffect(() => {
    // Clear stale keys immediately when the request identity changes so a
    // previous reservation's PINs can never render under a new identity.
    // The catch block intentionally does NOT clear, so same-identity
    // transient poll failures keep the last successful list.
    setRemotelockDoors([]);
    setLoading(true);

    // cancelled is set to true in cleanup so any in-flight response that
    // arrives after the identity has changed is silently dropped, preventing
    // a stale reservation's PINs from writing into the new identity's state.
    let cancelled = false;

    const fetchDoors = async () => {
      if (preview || !company || !guestId || !reservationId) {
        return;
      }

      try {
        const remotelockCodes =
          await ClientRemotelockService.getDoorsByReservationAndGuest(
            company,
            guestId,
            reservationId,
          );
        if (!cancelled) {
          setRemotelockDoors(remotelockCodes ?? []);
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        if (!cancelled) {
          console.error('[remotelock] fetchDoors error:', error);
          datadogRum.addError(error, { feature: 'remotelock-doors' });
          // Keep the last successful list visible on a transient poll failure; do not wipe valid keys.
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchDoors();

    const intervalId = setInterval(fetchDoors, 1000 * 30);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [preview, company, guestId, reservationId]);

  if (preview) {
    if (!settings.remotelock) {
      return null;
    }

    // preview Digital Keys component
    return (
      <DigitalKeysAvailable
        title={intl.formatMessage({
          id: 'digital_keys/pin_codes',
          defaultMessage: 'PIN Codes',
        })}
        type="remotelock"
        settings={settings}
        preview
      />
    );
  }

  const hasGuest = Boolean(guestId);
  if (
    loading ||
    !hasGuest ||
    !reservation ||
    reservationCheckedOut ||
    !settings.remotelock
  ) {
    // do not display Digital Keys component
    return null;
  }

  if (remotelockDoors.length === 0) {
    return (
      <NotAvailableStatus
        label={intl.formatMessage({
          id: 'digital_keys/pin_available_on_room_assignment',
          defaultMessage: 'PIN codes available once a room has been assigned.',
        })}
      />
    );
  }

  return (
    <DigitalKeysAvailable
      title={intl.formatMessage({
        id: 'digital_keys/pin_codes',
        defaultMessage: 'PIN codes',
      })}
      type="remotelock"
      settings={settings}
      items={remotelockDoors}
    />
  );
};
